import { useTranslation } from "react-i18next";
import { AccountBalance, LimitsStatus, ToggleSwitch } from "src/api";
import { formatNumberToLocale } from "src/utils";
import { Button, Field, InfoField, ErrorAlert, DropdownAnimation, IField } from "src/components";
import PaymentLimits from "src/components/paymentLimits/PaymentLimits";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import { ReactComponent as IconWallet } from "src/assets/icons/icon-wallet-2.svg";
import { EWithdrawalStep } from "src/types";
import { WithdrawalStep } from "src/hooks";
import { WithdrawalErrors } from "src/hooks/withdrawal";
import { useState } from "react";

interface IWithdrawalInitialStepProps {
  amount: string;
  handleAmountChange: (value: string) => void;
  errors: WithdrawalErrors;
  balance: AccountBalance;
  paymentFee: number;
  paymentFeeLoading: boolean;
  handleStepChange: (step: EWithdrawalStep) => void;
  withdrawalStep: WithdrawalStep;
  isRtcEnabled: ToggleSwitch | null;
  handleRtcChange: (enabled: ToggleSwitch) => void;
  limitsStatus: LimitsStatus | null;
  /** Tightest remaining headroom; Infinity = no cap configured. */
  availableLimit: number;
  limitsLoading: boolean;
}

const WithdrawalInitialStep: React.FC<IWithdrawalInitialStepProps> = ({
  amount,
  handleAmountChange,
  errors,
  balance,
  paymentFee,
  paymentFeeLoading,
  handleStepChange,
  withdrawalStep,
  isRtcEnabled,
  handleRtcChange,
  limitsStatus,
  availableLimit,
  limitsLoading,
}): JSX.Element => {
  const { t } = useTranslation();
  const [amountTouched, setAmountTouched] = useState(false);
  const {
    currency: { currency_id, precision },
    free_balance,
  } = balance;
  const { stepError, stepLoading } = withdrawalStep;

  const rtcNotSelected = isRtcEnabled === null;
  const submitDisabled =
    paymentFeeLoading || Object.values(errors).some((error) => error.length > 0) || !amount || !!stepError || rtcNotSelected;

  const errorMessages = [
    ...(rtcNotSelected ? [t("WITHDRAWAL_ERRORS.PLEASE_SELECT_RTC_OPTION")] : []),
    ...errors.inputErrors,
    ...errors.paymentValidationErrors,
  ];

  // `inputErrors` are the empty/non-positive cases, so they stay behind the
  // touch gate. `paymentValidationErrors` (limit exceeded, min amount, balance)
  // only fire for a real amount and surface immediately — otherwise the Next
  // button greys out with the reason hidden in its tooltip.
  const amountError = (amountTouched ? errors.inputErrors[0] : undefined) ?? errors.paymentValidationErrors[0];

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled) return;
    handleStepChange(EWithdrawalStep.BankSelection);
  };

  const renderFieldLabel = () => {
    return (
      <div className="field-label-content">
        <p>{t("WITHDRAWAL_STEPS.AMOUNT")}</p>
        <p>
          <IconWallet /> {formatNumberToLocale(free_balance, precision)} {currency_id}
        </p>
      </div>
    );
  };

  const rtcOptions: IField[] = [
    { key: ToggleSwitch.Off, value: t("WITHDRAWAL_STEPS.OFF") },
    { key: ToggleSwitch.On, value: t("WITHDRAWAL_STEPS.ON") },
  ];

  const onRtcChange = (option: IField) => {
    const newRtcValue = option.key as ToggleSwitch;
    handleRtcChange(newRtcValue);
  };

  const rtcLabel = (
    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {t("WITHDRAWAL_STEPS.REAL_TIME_CLEARING_RTC")}
      <IconTooltip
        iconType={IconTooltipType.INFO}
        tooltipId="rtc-tooltip"
        tooltipContent={t("WITHDRAWAL_STEPS.RTC_ENABLE_INSTANT_PAYMENTS")}
      />
    </span>
  );

  return (
    <form className="withdrawal-form" onSubmit={onSubmit}>
      <DropdownAnimation
        customLabel={rtcLabel}
        selectedValue={isRtcEnabled}
        options={rtcOptions}
        onChange={onRtcChange}
        disabled={stepLoading}
        placeholder={t("WITHDRAWAL_STEPS.SELECT_OPTION")}
      />

      <Field
        label={renderFieldLabel()}
        value={amount}
        onChange={(value) => handleAmountChange(value)}
        placeholder={t("WITHDRAWAL_STEPS.AMOUNT_PLACEHOLDER_ZERO")}
        disabled={stepLoading}
        error={amountError}
        onBlur={() => setAmountTouched(true)}
      />

      <InfoField
        label={t("WITHDRAWAL_STEPS.FEE")}
        value={`${formatNumberToLocale(paymentFee, precision)} ${currency_id}`}
        isLoading={paymentFeeLoading}
      />

      <InfoField
        label={t("WITHDRAWAL_STEPS.RECEIVE")}
        value={`${formatNumberToLocale(Number(amount) - Number(paymentFee), precision)} ${currency_id}`}
        isLoading={paymentFeeLoading}
      />

      <PaymentLimits
        limitsStatus={limitsStatus}
        availableLimit={availableLimit}
        currency={currency_id}
        isLoading={limitsLoading}
      />

      <ErrorAlert message={stepError} />

      <Button
        title={t("WITHDRAWAL_STEPS.NEXT_STEP")}
        className="withdrawal-button"
        type="submit"
        disabled={submitDisabled}
        pending={paymentFeeLoading || stepLoading}
        tooltipMessage={errorMessages[0]}
      />
    </form>
  );
};

export default WithdrawalInitialStep;
